/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import {useCallback, useMemo, useState} from 'react';

/**
 * useVideoPlayer is a custom hook to handle video player state.
 * 
 * ```typescript
 * 
 * import useVideoPlayer from './hooks/useVideoPlayer';
 *  
 * const {
 *  handleVolumeChange,
 *  handleVolumeIcon,
 *  handleVideoHover,
 *  handleVideoLeave,
 *  handlePlayPause,
 *  handleProgress,
 *  handleDuration,
 *  handleEnded,
 *  isHovering,
 *  setFailed,
 *  duration,
 *  restart,
 *  playing,
 *  failed,
 *  volume,
 *  retry
 * } = useVideoPlayer();
 * 
 * <ReactPlayer ref={ref}
 *   onProgress={handleProgress}
 *   onDuration={handleDuration}
 *   duration={duration}
 *   onEnded={handleEnded}
 *   playing={playing || isHovering}
 *   volume={volume}
 *   url={urls[0]}
 *   width='100%'
 *   height='auto'
 *   onError={function() {
 *   setFailed(true);
 *   }} />
 * 
 * ```
 * 
 * @returns {Object} - Returns an object with all the state and functions to handle the video player.
 */
export const useVideoPlayer = () => {
  const [volume, setVolume]         = useState(0.8);
  const [prevVolume, setPrevVolume] = useState(0.8);
  const [playing, setPlaying]       = useState(false);
  const [duration, setDuration]     = useState(null);
  const [progress, setProgress]     = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [failed, setFailed]         = useState(false);
  const [restart, setRestart]       = useState(false);
  const [isReady, setIsReady]       = useState(false);

  // ** Retry (On Error) Handler **
  const retry = useCallback(() => {
    setFailed(false);
  }, []);

  // ** Volume Handlers **
  const sliderChange = useCallback(playedSeconds => {
    setProgress(playedSeconds);
  }, []);

  const handleVolumeChange = useCallback((event, value) => {
    setPrevVolume(volume);
    const vol = parseFloat(value);
    setVolume(vol);
  }, [volume]);

  const handleVolumeIcon = useCallback(() => {
    setPrevVolume(volume);
    const currVolume = volume === 0 ? prevVolume : 0;
    setVolume(currVolume);
  }, [prevVolume, volume]);

  // ** Play/Pause Handlers **
  const handlePlayPause = useCallback(() => {
    setRestart(false);
    setPlaying(prevPlaying => !prevPlaying);
  }, []);

  // React player API called when video is in progress
  const handleProgress = useCallback(state => {
    sliderChange(state.playedSeconds);
  }, [sliderChange]);

  // React player API to find the video duration
  const handleDuration = useCallback(duration => {
    setDuration(duration);
  }, []);

  // React Player API called when video ended
  const handleEnded = useCallback(() => {
    setRestart(true);
    setPlaying(false);
  }, []);

  const handlePlay = useCallback(() => {
    setRestart(false);
    setPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setPlaying(false);
  }, []);

  const handleVideoHover = useCallback(() => {
    setRestart(false);
    setIsHovering(true);
  }, []);

  const handleVideoLeave = useCallback(() => {
    setIsHovering(false);
  }, []);

  const handleIsReady = useCallback(() => {
    setIsReady(true);
  }, []);

  return useMemo(() => ({
    handleVolumeChange,
    handleVolumeIcon,
    handleVideoHover,
    handleVideoLeave,
    handlePlayPause,
    handleProgress,
    handleDuration,
    handleIsReady,
    handleEnded,
    handlePause,
    handlePlay,
    isHovering,
    setPlaying,
    setRestart,
    setFailed,
    progress,
    duration,
    restart,
    isReady,
    playing,
    failed,
    volume,
    retry
  }), [
    handleVolumeChange,
    handleVolumeIcon,
    handleVideoHover,
    handleVideoLeave,
    handlePlayPause,
    handleProgress,
    handleDuration,
    handleIsReady,
    handleEnded,
    handlePause,
    handlePlay,
    isHovering,
    setPlaying,
    setRestart,
    setFailed,
    progress,
    duration,
    isReady,
    restart,
    playing,
    failed,
    volume,
    retry
  ]);
};

export default useVideoPlayer;
